import dialogTemplate from './dialog.html?raw';

export class Dialog extends HTMLElement {
    static get observedAttributes() {
        return ['open', 'title', 'text'];
    }

    constructor() {
        super();

        this.dialog = null;
        this.closeButton = null;
    }

    connectedCallback() {
        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = dialogTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        this.dialog = this.shadowRoot.querySelector('dialog');

        const topCloseButton = this.shadowRoot.querySelector('.close');

        if (topCloseButton) {
            topCloseButton.addEventListener('click', () => this.closeDialog());

            if (this.hasAttribute('close-aria-label')) {
                topCloseButton.setAttribute(
                    'aria-label',
                    this.getAttribute('close-aria-label')
                );
            }
        }

        if (this.hasAttribute('title')) {
            const title = this.shadowRoot.querySelector('h2');
            title.textContent = this.getAttribute('title');
        }

        if (this.hasAttribute('text')) {
            const title = this.shadowRoot.querySelector('h2');

            const textParagraph = document.createElement('p');
            textParagraph.innerText = this.getAttribute('text');
            textParagraph.id = 'dialog-text';

            title.insertAdjacentElement('afterend', title);
        }

        this.closeButton = this.shadowRoot.querySelector(
            '.button-group button'
        );
        this.closeButton.innerHTML = this.getAttribute('close') || 'Close';
        this.closeButton.addEventListener('click', () => this.closeDialog());

        if (
            this.hasAttribute('open') &&
            this.getAttribute('open') !== 'false'
        ) {
            this.dialog.showModal();
        }
    }

    showDialog() {
        const focusableElements = this.getFocusableElements();

        if (focusableElements.length > 0) {
            focusableElements[0].focus();
        }

        this.dialog.addEventListener('keydown', (event) =>
            this.handleKeyDown(event)
        );

        this.dialog.showModal();

        this.closeButton.focus();
    }

    closeDialog() {
        this.dialog.removeEventListener('keydown', (event) =>
            this.handleKeyDown(event)
        );

        this.dialog.close();

        this.emit('close');
    }

    handleKeyDown(e) {
        if (e.key === 'Escape') {
            this.closeDialog();
        } else if (e.key === 'Tab') {
            const focusableElements = this.getFocusableElements();

            if (focusableElements.length === 0) return;

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement =
                focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === firstFocusableElement) {
                    e.preventDefault();
                    lastFocusableElement.focus();
                }
            } else {
                if (document.activeElement === lastFocusableElement) {
                    e.preventDefault();
                    firstFocusableElement.focus();
                }
            }
        }
    }

    getFocusableElements() {
        return Array.from(
            this.dialog.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
        );
    }

    attributeChangedCallback(name, _, newValue) {
        if (name === 'open' && this.shadowRoot) {
            if (newValue !== null && newValue !== 'false') {
                this.showDialog();
            } else {
                this.closeDialog();
            }
        } else if (name === 'title') {
            const title = this.shadowRoot?.querySelector('h2');
            if (title) {
                title.innerText = newValue;
            }
        } else if (name === 'text') {
            let text = this.shadowRoot?.querySelector('.dialog-content p');
            if (!text) {
                text = document.createElement('p');

                const title = this.shadowRoot?.querySelector('h2');
                if (title) {
                    title.insertAdjacentElement('afterend', text);
                }
            }

            text.innerText = newValue;
        } else if (name === 'close-aria-label') {
            const closeButton = this.shadowRoot?.querySelector('.close');
            if (closeButton) {
                closeButton.setAttribute('aria-label', newValue ?? 'Close');
            }
        }
    }

    emit(type, detail = {}) {
        let event = new CustomEvent(`dialog:${type}`, {
            bubbles: true,
            cancelable: true,
            detail: detail,
        });

        return this.dispatchEvent(event);
    }
}

customElements.define('svea-dialog', Dialog);
